/**
 * PhraseEditModal Component
 * Distilled modal for editing phrase content in creator mode.
 * - Phrase text as prominent header input
 * - Type chips (not dropdown)
 * - Side-by-side bilingual definitions
 * - Optional fields behind progressive disclosure
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { PHRASE_TYPES } from '../../types/creator-workflow';
import type { PhraseEntry } from '../../types/creator-workflow';
import './PhraseEditModal.css';

interface PhraseEditModalProps {
  phrase: PhraseEntry;
  phraseIndex: number;
  onSave: (index: number, updates: Partial<PhraseEntry>) => Promise<void>;
  onClose: () => void;
  isLoading?: boolean;
}

const PhraseEditModal: React.FC<PhraseEditModalProps> = ({
  phrase,
  phraseIndex,
  onSave,
  onClose,
  isLoading = false,
}) => {
  const { t } = useTranslation(['creator', 'common']);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const firstInputRef = useRef<HTMLInputElement>(null);

  // Form state
  const [text, setText] = useState(phrase.text || '');
  const [type, setType] = useState(phrase.type || 'expression');
  const [definitionEn, setDefinitionEn] = useState(phrase.definition_en || '');
  const [definitionZhTw, setDefinitionZhTw] = useState(phrase.definition_native || '');
  const [explanationZhTw, setExplanationZhTw] = useState(phrase.explanation_native || '');
  const [usageExample, setUsageExample] = useState(phrase.usage_example || '');

  // Progressive disclosure — expand if optional fields have content
  const [showMore, setShowMore] = useState(
    !!(phrase.explanation_native || phrase.usage_example)
  );

  // Handle escape key to close modal
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape' && !saving) {
      onClose();
    }
  }, [onClose, saving]);

  // Focus trap and escape key handling
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement;
    firstInputRef.current?.focus();
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocused?.focus();
    };
  }, [handleKeyDown]);

  // Reset form when phrase changes
  useEffect(() => {
    setText(phrase.text || '');
    setType(phrase.type || 'expression');
    setDefinitionEn(phrase.definition_en || '');
    setDefinitionZhTw(phrase.definition_native || '');
    setExplanationZhTw(phrase.explanation_native || '');
    setUsageExample(phrase.usage_example || '');
    setShowMore(!!(phrase.explanation_native || phrase.usage_example));
  }, [phrase]);

  const handleSave = async () => {
    if (!text.trim()) {
      setError(t('creator:workflow.phraseEditModal.phraseRequired'));
      return;
    }
    if (!definitionEn.trim()) {
      setError(t('creator:workflow.phraseEditModal.definitionEnRequired'));
      return;
    }
    if (!definitionZhTw.trim()) {
      setError(t('creator:workflow.phraseEditModal.definitionZhRequired'));
      return;
    }
    if (text.length > 500) {
      setError(t('creator:workflow.phraseEditModal.phraseTooLong'));
      return;
    }
    if (definitionEn.length > 1000) {
      setError(t('creator:workflow.phraseEditModal.definitionEnTooLong'));
      return;
    }
    if (definitionZhTw.length > 1000) {
      setError(t('creator:workflow.phraseEditModal.definitionZhTooLong'));
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const updates: Partial<PhraseEntry> = {
        text: text.trim(),
        type,
        definition_en: definitionEn.trim(),
        definition_native: definitionZhTw.trim(),
        explanation_native: explanationZhTw.trim() || undefined,
        usage_example: usageExample.trim() || undefined,
      };

      await onSave(phraseIndex, updates);
      onClose();
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError(t('creator:workflow.phraseEditModal.failedToSave'));
      }
    } finally {
      setSaving(false);
    }
  };

  const charIndicator = (value: string, max: number) => {
    const ratio = value.length / max;
    const className = ratio > 0.9 ? 'char-count warn' : 'char-count';
    return value.length > 0 ? (
      <span className={className}>
        {t('creator:workflow.phraseEditModal.charCount', { count: value.length, max })}
      </span>
    ) : null;
  };

  return (
    <div
      className="phrase-edit-modal-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="phrase-modal-title"
    >
      <div
        ref={modalRef}
        className="phrase-edit-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header: phrase input as the title */}
        <div className="pem-header">
          <div className="pem-header-input">
            <input
              ref={firstInputRef}
              id="phrase-text"
              type="text"
              className="pem-phrase-input"
              value={text}
              onChange={(e) => { setText(e.target.value); setError(null); }}
              placeholder={t('creator:workflow.phraseEditModal.phrasePlaceholder')}
              aria-label={t('creator:workflow.phraseEditModal.phraseText')}
            />
            {charIndicator(text, 500)}
          </div>
          <button
            type="button"
            className="pem-close"
            onClick={onClose}
            aria-label={t('creator:workflow.phraseEditModal.closeModal')}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Type chips */}
        <div className="pem-type-chips" role="radiogroup" aria-label={t('creator:workflow.phraseEditModal.type')}>
          {PHRASE_TYPES.map((pt) => (
            <button
              key={pt}
              type="button"
              role="radio"
              aria-checked={type === pt}
              className={`pem-chip${type === pt ? ' active' : ''}`}
              onClick={() => setType(pt)}
            >
              {t(`creator:workflow.phraseEditModal.phraseTypes.${pt}`, { defaultValue: pt.replace('_', ' ') })}
            </button>
          ))}
        </div>

        {error && <div className="pem-error">{error}</div>}

        {/* Definitions — side by side */}
        <div className="pem-body">
          <div className="pem-section-label" id="phrase-modal-title">
            {t('creator:workflow.phraseEditModal.definitions')}
          </div>
          <div className="pem-definitions">
            <div className="pem-def-col">
              <label className="pem-label" htmlFor="def-en">
                {t('creator:workflow.phraseEditModal.definitionEn')}
              </label>
              <textarea
                id="def-en"
                value={definitionEn}
                onChange={(e) => { setDefinitionEn(e.target.value); setError(null); }}
                placeholder={t('creator:workflow.phraseEditModal.definitionEnPlaceholder')}
                rows={2}
              />
              {charIndicator(definitionEn, 1000)}
            </div>
            <div className="pem-def-col">
              <label className="pem-label" htmlFor="def-zh">
                {t('creator:workflow.phraseEditModal.definitionZh')}
              </label>
              <textarea
                id="def-zh"
                value={definitionZhTw}
                onChange={(e) => { setDefinitionZhTw(e.target.value); setError(null); }}
                placeholder={t('creator:workflow.phraseEditModal.definitionZhPlaceholder')}
                rows={2}
              />
              {charIndicator(definitionZhTw, 1000)}
            </div>
          </div>

          {/* Progressive disclosure for optional fields */}
          {!showMore ? (
            <button
              type="button"
              className="pem-toggle-more"
              onClick={() => setShowMore(true)}
            >
              + {t('creator:workflow.phraseEditModal.moreDetails')}
            </button>
          ) : (
            <div className="pem-optional-fields">
              <div className="pem-field">
                <label className="pem-label" htmlFor="explanation">
                  {t('creator:workflow.phraseEditModal.explanationZh')}
                </label>
                <textarea
                  id="explanation"
                  value={explanationZhTw}
                  onChange={(e) => setExplanationZhTw(e.target.value)}
                  placeholder={t('creator:workflow.phraseEditModal.explanationZhPlaceholder')}
                  rows={2}
                />
              </div>
              <div className="pem-field">
                <label className="pem-label" htmlFor="usage">
                  {t('creator:workflow.phraseEditModal.usageExample')}
                </label>
                <textarea
                  id="usage"
                  value={usageExample}
                  onChange={(e) => setUsageExample(e.target.value)}
                  placeholder={t('creator:workflow.phraseEditModal.usageExamplePlaceholder')}
                  rows={2}
                />
              </div>
              <button
                type="button"
                className="pem-toggle-more"
                onClick={() => setShowMore(false)}
              >
                {t('creator:workflow.phraseEditModal.lessDetails')}
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="pem-footer">
          <button className="pem-btn-cancel" onClick={onClose} disabled={saving}>
            {t('common:cancel')}
          </button>
          <button
            className="pem-btn-save"
            onClick={handleSave}
            disabled={saving || isLoading}
          >
            {saving ? t('common:saving') : t('creator:workflow.phraseEditModal.saveChanges')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PhraseEditModal;
